// =============================================================================
// 🚀 9G Platform - Frontend Environment Configuration
// =============================================================================
// Centralized environment configuration for frontend
// Only uses import.meta.env (Vite standard) - NO process.env
// All services should import from this single source

// Environment variables type definition for Vite
interface ImportMetaEnv {
  readonly VITE_API_BASE_URL: string
  readonly VITE_SMART_OPS_BACKEND_URL: string
  readonly VITE_SMART_OPS_BACKEND_WS_URL: string
  readonly VITE_AI_SERVICE_URL: string
  readonly VITE_RAG_SERVICE_URL: string
  readonly VITE_FRONTEND_URL: string
  readonly VITE_FRONTEND_WS_URL: string
  readonly VITE_FRONTEND_PORT: string
  readonly VITE_SMART_OPS_BACKEND_PORT: string
  readonly VITE_AI_SERVICE_PORT: string
  readonly VITE_RAG_SERVICE_PORT: string
  readonly VITE_WS_BASE_URL: string
  readonly VITE_NODE_ENV: string
  readonly VITE_LOG_LEVEL: string
  readonly VITE_TEST_TIMEOUT: string
  readonly VITE_TEST_RETRIES: string
}

// Extend ImportMeta interface for Vite
interface ImportMeta {
  readonly env: ImportMetaEnv
}

// Single source of truth for all environment configuration
export const ENV_CONFIG = {
  // =============================================================================
  // API BASE URLs
  // =============================================================================
  API_BASE_URL: (import.meta as any).env?.VITE_API_BASE_URL || '/api',
  SMART_OPS_BACKEND_URL: (import.meta as any).env?.VITE_SMART_OPS_BACKEND_URL || 'http://localhost:8000',
  SMART_OPS_BACKEND_WS_URL: (import.meta as any).env?.VITE_SMART_OPS_BACKEND_WS_URL || 'ws://localhost:8000',
  AI_SERVICE_URL: (import.meta as any).env?.VITE_AI_SERVICE_URL || 'http://localhost:8001',
  RAG_SERVICE_URL: (import.meta as any).env?.VITE_RAG_SERVICE_URL || 'http://localhost:8002',
  FRONTEND_URL: (import.meta as any).env?.VITE_FRONTEND_URL || 'http://localhost:9101',
  FRONTEND_WS_URL: (import.meta as any).env?.VITE_FRONTEND_WS_URL || 'ws://localhost:9101',
  WS_BASE_URL: (import.meta as any).env?.VITE_WS_BASE_URL || '/ws',

  // =============================================================================
  // SERVICE PORTS
  // =============================================================================
  SMART_OPS_BACKEND_PORT: parseInt((import.meta as any).env?.VITE_SMART_OPS_BACKEND_PORT || '8000'),
  SMART_OPS_FRONTEND_PORT: parseInt((import.meta as any).env?.VITE_SMART_OPS_FRONTEND_PORT || '9101'),
  AI_SERVICE_PORT: parseInt((import.meta as any).env?.VITE_AI_SERVICE_PORT || '8001'),
  RAG_SERVICE_PORT: parseInt((import.meta as any).env?.VITE_RAG_SERVICE_PORT || '8002'),

  // =============================================================================
  // DEVELOPMENT CONFIGURATION
  // =============================================================================
  NODE_ENV: (import.meta as any).env?.VITE_NODE_ENV || 'development',
  LOG_LEVEL: (import.meta as any).env?.VITE_LOG_LEVEL || 'info',

  // =============================================================================
  // TEST CONFIGURATION
  // =============================================================================
  TEST_TIMEOUT: parseInt((import.meta as any).env?.VITE_TEST_TIMEOUT || '30000'),
  TEST_RETRIES: parseInt((import.meta as any).env?.VITE_TEST_RETRIES || '3'),

  // =============================================================================
  // API CONFIGURATION
  // =============================================================================
  API_VERSION: 'v1',
  API_TIMEOUT: 30000, // 30 seconds
  API_RETRY_ATTEMPTS: 3,
  API_RETRY_DELAY: 1000, // 1 second

  // =============================================================================
  // FEATURE FLAGS
  // =============================================================================
  ENABLE_MOCK_DATA: false,
  ENABLE_DEBUG_MODE: true,
  ENABLE_ANALYTICS: false,

  // =============================================================================
  // ENVIRONMENT DETECTION
  // =============================================================================
  IS_DEVELOPMENT: true,
  IS_STAGING: false,
  IS_PRODUCTION: false,
  IS_TEST: false,

  // =============================================================================
  // SERVICE MAPPING
  // =============================================================================
  SERVICES: {
    'smart-ops': {
      name: 'Smart-Ops Backend',
      port: parseInt((import.meta as any).env?.VITE_SMART_OPS_BACKEND_PORT || '8000'),
      url: (import.meta as any).env?.VITE_SMART_OPS_BACKEND_URL || 'http://localhost:8000',
      wsUrl: (import.meta as any).env?.VITE_SMART_OPS_BACKEND_WS_URL || 'ws://localhost:8000',
      healthEndpoint: '/health',
    },
    'smart-ops-frontend': {
      name: 'Smart-Ops Frontend',
      port: parseInt((import.meta as any).env?.VITE_SMART_OPS_FRONTEND_PORT || '9101'),
      url: (import.meta as any).env?.VITE_FRONTEND_URL || 'http://localhost:9101',
      wsUrl: (import.meta as any).env?.VITE_FRONTEND_WS_URL || 'ws://localhost:9101',
      healthEndpoint: '/',
    },
    'ai-service': {
      name: 'AI Service',
      port: parseInt((import.meta as any).env?.VITE_AI_SERVICE_PORT || '8001'),
      url: (import.meta as any).env?.VITE_AI_SERVICE_URL || 'http://localhost:8001',
      wsUrl: undefined, // AI service doesn't have WebSocket
      healthEndpoint: '/health',
    },
    'rag-service': {
      name: 'RAG Service',
      port: parseInt((import.meta as any).env?.VITE_RAG_SERVICE_PORT || '8002'),
      url: (import.meta as any).env?.VITE_RAG_SERVICE_URL || 'http://localhost:8002',
      wsUrl: undefined, // RAG service doesn't have WebSocket
      healthEndpoint: '/health',
    },

  } as const,

  // =============================================================================
  // API MODE
  // =============================================================================
  API_MODE: 'live' as 'mock' | 'live', // Default to live mode for real AI

} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Get service configuration
export const getServiceConfig = (serviceName: keyof typeof ENV_CONFIG.SERVICES) => {
  return ENV_CONFIG.SERVICES[serviceName];
};

// Get service URL
export const getServiceURL = (serviceName: keyof typeof ENV_CONFIG.SERVICES) => {
  return ENV_CONFIG.SERVICES[serviceName].url;
};

// Get service port
export const getServicePort = (serviceName: keyof typeof ENV_CONFIG.SERVICES) => {
  return ENV_CONFIG.SERVICES[serviceName].port;
};

// Get service WebSocket URL
export const getServiceWSURL = (serviceName: keyof typeof ENV_CONFIG.SERVICES) => {
  const service = ENV_CONFIG.SERVICES[serviceName];
  return service.wsUrl || null;
};

// Environment detection helpers
export const isDevelopment = (): boolean => ENV_CONFIG.IS_DEVELOPMENT;
export const isProduction = (): boolean => ENV_CONFIG.IS_PRODUCTION;
export const isStaging = (): boolean => ENV_CONFIG.IS_STAGING;
export const isTest = (): boolean => ENV_CONFIG.IS_TEST;

// API mode helpers
export const getApiMode = (): 'mock' | 'live' => ENV_CONFIG.API_MODE;
export const isMockMode = (): boolean => ENV_CONFIG.API_MODE === 'mock' as const;
export const isLiveMode = (): boolean => ENV_CONFIG.API_MODE === 'live' as const;

// Feature flag helpers
export const isMockDataEnabled = (): boolean => ENV_CONFIG.ENABLE_MOCK_DATA;
export const isDebugModeEnabled = (): boolean => ENV_CONFIG.ENABLE_DEBUG_MODE;
export const isAnalyticsEnabled = (): boolean => ENV_CONFIG.ENABLE_ANALYTICS;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type ServiceName = keyof typeof ENV_CONFIG.SERVICES;
export type ApiMode = 'mock' | 'live';
export type Environment = 'development' | 'staging' | 'production' | 'test'; 